import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle, Shield, Building,
  Zap, Award, Users, FileText, Download, Star, Home, Key, Wrench,
  AlertTriangle, Euro, Truck, Calendar
} from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import SEO from '../seo/SEO';

const PartenariatAgencesPage = () => {
  const pageTitle = "Partenariat Agences Immobilières | NGL SERVICES";
  const pageDescription = "NGL SERVICES, votre partenaire vitrerie, menuiserie et serrurerie pour agences immobilières. Intervention 24h/24, devis gratuit, tarifs professionnels. Val-de-Marne (94).";

  // Services Vitrerie
  const servicesVitrerie = [
    "Remplacement de vitre cassée",
    "Intervention bris de glace",
    "Mise en sécurité après effraction",
    "Vitrage simple, double et triple vitrage",
    "Vitrage feuilleté sécurité",
    "Vitrines de commerces",
    "Baies vitrées",
    "Miroirs sur mesure",
    "Vitrage pour parties communes d'immeubles"
  ];

  // Services Menuiserie
  const servicesMenuiserie = {
    fenetres: [
      "Fenêtres PVC, Aluminium, Bois",
      "Fenêtres oscillo-battantes",
      "Baies vitrées coulissantes",
      "Rénovation et remplacement"
    ],
    portes: [
      "Porte d'entrée (PVC, alu, bois)",
      "Porte de hall d'immeuble",
      "Portes sécurisées / techniques",
      "Remplacement après sinistre"
    ],
    volets: [
      "Volets roulants manuels / motorisés",
      "Volets battants",
      "Motorisation volets existants",
      "Dépannage volet bloqué"
    ]
  };

  // Services Serrurerie
  const servicesSerrurerie = [
    { service: "Ouverture de porte claquée", prix: "90 €" },
    { service: "Ouverture de porte fermée à clé", prix: "Sur devis" },
    { service: "Changement de serrure", prix: "Sur devis" },
    { service: "Serrure multipoints", prix: "Sur devis" },
    { service: "Cylindre haute sécurité", prix: "Sur devis" },
    { service: "Blindage de porte", prix: "Sur devis" },
    { service: "Mise en sécurité après effraction", prix: "Sur devis" },
    { service: "Serrurerie immeuble / parties communes", prix: "Sur devis" }
  ];

  // Références / Partenaires fictifs
  const references = [
    "Century 21",
    "Orpi",
    "Laforêt",
    "Guy Hoquet",
    "ERA Immobilier",
    "Syndics de copropriété"
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title={pageTitle} description={pageDescription} canonical="/partenariat-agences-immobilieres" />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.menuisiervitrier.fr/partenariat-agences-immobilieres" />
      </Helmet>

      {/* Header avec logo et coordonnées */}
      <section className="bg-[hsl(215,25%,15%)] text-white py-8 print:py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ngl-services/artifacts/f9kn98hp_LOGO%20NGL%20SERVICES.png"
                alt="NGL SERVICES - Logo"
                className="h-20 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold">NGL SERVICES</h1>
                <p className="text-gray-300">Vitrier • Menuisier • Serrurier</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
              <div>
                <p className="text-sm text-gray-400">Urgences 24h/24</p>
                <a href="tel:0781559050" className="text-2xl font-bold text-[hsl(24,95%,53%)]">
                  07 81 55 90 50
                </a>
              </div>
              <div className="hidden sm:block w-px h-12 bg-gray-600" />
              <div>
                <p className="text-sm text-gray-400">Standard</p>
                <a href="tel:0184211311" className="text-xl font-semibold">
                  01 84 21 13 11
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau Devis Gratuit */}
      <section className="bg-[hsl(24,95%,53%)] text-white py-4 print:py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6" />
              <span className="text-lg font-bold">DEVIS GRATUIT</span>
            </div>
            <div className="hidden sm:block w-2 h-2 rounded-full bg-white/50" />
            <div className="flex items-center gap-2">
              <Truck className="w-6 h-6" />
              <span className="text-lg font-bold">DÉPLACEMENT GRATUIT</span>
            </div>
            <div className="hidden sm:block w-2 h-2 rounded-full bg-white/50" />
            <div className="flex items-center gap-2">
              <Clock className="w-6 h-6" />
              <span className="text-lg font-bold">DISPONIBLE 24H/24</span>
            </div>
          </div>
        </div>
      </section>

      {/* Titre principal */}
      <section className="py-12 bg-gradient-to-b from-[hsl(210,20%,98%)] to-white print:py-6">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-[hsl(207,78%,42%)] text-white mb-4 px-4 py-2">
            <Building className="w-4 h-4 mr-2" /> Offre Professionnels & Agences
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
            Partenariat Agences Immobilières
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            <strong>NGL SERVICES</strong>, votre partenaire de confiance pour tous vos besoins en 
            <strong> vitrerie</strong>, <strong>menuiserie extérieure</strong> et <strong>serrurerie</strong>. 
            Réactivité, qualité et tarifs professionnels pour les agences immobilières du Val-de-Marne.
          </p>
        </div>
      </section>

      {/* Nos engagements */}
      <section className="py-10 print:py-4">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Réactivité", desc: "Intervention rapide sous 1h pour les urgences" },
              { icon: Clock, title: "Disponibilité", desc: "24h/24 – 7j/7 y compris jours fériés" },
              { icon: Euro, title: "Transparence", desc: "Devis détaillé gratuit, pas de surprise" },
              { icon: Shield, title: "Garantie", desc: "Travaux garantis, assurance décennale" }
            ].map((item, idx) => (
              <Card key={idx} className="border-2 border-[hsl(207,78%,90%)] hover:border-[hsl(207,78%,42%)] transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-[hsl(207,78%,42%)]" />
                  </div>
                  <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SECTION VITRERIE ========== */}
      <section className="py-12 bg-[hsl(210,20%,98%)] print:py-6 print:break-before-page">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[hsl(207,78%,42%)] flex items-center justify-center">
              <Home className="w-8 h-8 text-white" />
            </div>
            <div>
              <Badge className="bg-[hsl(24,95%,53%)] text-white mb-1">Service prioritaire</Badge>
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)]">
                1. VITRERIE – Dépannage & Installation
              </h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <p className="text-muted-foreground mb-6">
                Service de vitrerie complet pour vos biens en gestion : intervention rapide en cas de 
                bris de glace, mise en sécurité après effraction, remplacement de vitrage pour 
                appartements, maisons et parties communes d'immeubles.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {servicesVitrerie.map((service, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)] flex-shrink-0" />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="font-bold text-[hsl(215,25%,15%)] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                Urgences Vitrerie 24h/24
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between p-3 bg-[hsl(210,20%,98%)] rounded-lg">
                  <span>Mise en sécurité provisoire</span>
                  <span className="font-bold text-[hsl(207,78%,42%)]">Sur devis</span>
                </li>
                <li className="flex items-center justify-between p-3 bg-[hsl(210,20%,98%)] rounded-lg">
                  <span>Remplacement vitre simple</span>
                  <span className="font-bold text-[hsl(207,78%,42%)]">Sur devis</span>
                </li>
                <li className="flex items-center justify-between p-3 bg-[hsl(210,20%,98%)] rounded-lg">
                  <span>Double vitrage standard</span>
                  <span className="font-bold text-[hsl(207,78%,42%)]">Sur devis</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mt-4 italic">
                * Devis gratuit et sans engagement. Prix indicatifs, variables selon dimensions et type de vitrage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION MENUISERIE ========== */}
      <section className="py-12 print:py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[hsl(207,78%,42%)] flex items-center justify-center">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)]">
                2. MENUISERIE EXTÉRIEURE – Toutes Activités
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Fenêtres */}
            <Card className="border-t-4 border-t-[hsl(207,78%,42%)]">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-[hsl(215,25%,15%)] mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center text-sm font-bold text-[hsl(207,78%,42%)]">F</span>
                  Fenêtres
                </h3>
                <ul className="space-y-2">
                  {servicesMenuiserie.fenetres.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Portes */}
            <Card className="border-t-4 border-t-[hsl(24,95%,53%)]">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-[hsl(215,25%,15%)] mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-[hsl(24,95%,95%)] flex items-center justify-center text-sm font-bold text-[hsl(24,95%,53%)]">P</span>
                  Portes
                </h3>
                <ul className="space-y-2">
                  {servicesMenuiserie.portes.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Volets */}
            <Card className="border-t-4 border-t-[hsl(215,25%,50%)]">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-[hsl(215,25%,15%)] mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-[hsl(215,25%,95%)] flex items-center justify-center text-sm font-bold text-[hsl(215,25%,50%)]">V</span>
                  Volets
                </h3>
                <ul className="space-y-2">
                  {servicesMenuiserie.volets.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 p-4 bg-[hsl(207,78%,95%)] rounded-xl text-center">
            <p className="text-sm text-[hsl(207,78%,35%)]">
              <strong>+ Autres menuiseries :</strong> Garde-corps, fermetures extérieures, menuiserie sur mesure
            </p>
          </div>
        </div>
      </section>

      {/* ========== SECTION SERRURERIE ========== */}
      <section className="py-12 bg-[hsl(210,20%,98%)] print:py-6 print:break-before-page">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[hsl(207,78%,42%)] flex items-center justify-center">
              <Key className="w-8 h-8 text-white" />
            </div>
            <div>
              <Badge className="bg-[hsl(24,95%,53%)] text-white mb-1">Urgence 24h/24</Badge>
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)]">
                3. SERRURERIE – Dépannage & Sécurité
              </h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <p className="text-muted-foreground mb-6">
                Service de serrurerie d'urgence pour vos locataires et copropriétés : ouverture de porte, 
                changement de serrure, mise en sécurité. Intervention rapide et tarifs transparents.
              </p>
              
              <div className="bg-[hsl(24,95%,53%)] text-white rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm">Ouverture porte claquée</p>
                    <p className="text-3xl font-bold">À partir de 90 €</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/80 text-sm">Intervention</p>
                    <p className="text-xl font-bold">{"< 1 heure"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-[hsl(215,25%,15%)] text-white p-4">
                <h3 className="font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Grille tarifaire indicative
                </h3>
              </div>
              <div className="p-4">
                <table className="w-full text-sm">
                  <tbody>
                    {servicesSerrurerie.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-[hsl(210,20%,98%)]' : ''}>
                        <td className="p-3">{item.service}</td>
                        <td className="p-3 text-right font-bold text-[hsl(207,78%,42%)]">{item.prix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ILS NOUS FONT CONFIANCE ========== */}
      <section className="py-12 print:py-6">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-2">
              Ils nous font confiance
            </h2>
            <p className="text-muted-foreground">
              NGL SERVICES accompagne de nombreux professionnels de l'immobilier
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8">
            {references.map((ref, idx) => (
              <div key={idx} className="px-6 py-4 bg-[hsl(210,20%,98%)] rounded-xl">
                <p className="font-semibold text-[hsl(215,25%,30%)]">{ref}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <p className="text-4xl font-bold text-[hsl(207,78%,42%)]">+500</p>
              <p className="text-muted-foreground">Interventions/an</p>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold text-[hsl(24,95%,53%)]">98%</p>
              <p className="text-muted-foreground">Clients satisfaits</p>
            </div>
            <div className="p-6">
              <p className="text-4xl font-bold text-[hsl(207,78%,42%)]">{"<1h"}</p>
              <p className="text-muted-foreground">Délai d'intervention urgence</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COORDONNÉES & CONTACT ========== */}
      <section className="py-12 bg-[hsl(215,25%,15%)] text-white print:py-6">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold mb-6">Nos coordonnées</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[hsl(24,95%,53%)] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Siège social</p>
                    <p className="text-gray-300">27 avenue André Rouy</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Building className="w-6 h-6 text-[hsl(24,95%,53%)] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Atelier</p>
                    <p className="text-gray-300">43 rue Paul Bert, 94400 Vitry-sur-Seine</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-[hsl(24,95%,53%)] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Téléphones</p>
                    <p className="text-[hsl(24,95%,53%)] text-xl font-bold">Urgence : 07 81 55 90 50</p>
                    <p className="text-gray-300">Standard : 01 84 21 13 11</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-[hsl(24,95%,53%)] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-gray-300">comptaglotech@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Globe className="w-6 h-6 text-[hsl(24,95%,53%)] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Site internet</p>
                    <p className="text-[hsl(207,78%,60%)]">www.menuisiervitrier.fr</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-6">Devenez partenaire</h2>
              <p className="text-gray-300 mb-6">
                Vous êtes une agence immobilière, un syndic de copropriété ou un gestionnaire de biens ? 
                Contactez-nous pour établir un partenariat et bénéficier de conditions privilégiées.
              </p>
              <div className="space-y-4">
                <Link to="/contact">
                  <Button size="lg" className="w-full emergency-btn rounded-full">
                    <Mail className="w-5 h-5 mr-2" />
                    Nous contacter pour un partenariat
                  </Button>
                </Link>
                <a href="tel:0184211311">
                  <Button size="lg" variant="outline" className="w-full border-white text-white hover:bg-white/10 rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Appeler le standard : 01 84 21 13 11
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer léger */}
      <section className="py-6 bg-[hsl(215,25%,10%)] text-center text-gray-400 text-sm">
        <div className="container mx-auto px-4">
          <p>© {new Date().getFullYear()} NGL SERVICES – Vitrier, Menuisier, Serrurier – Val-de-Marne (94)</p>
          <p className="mt-1">Disponible 24h/24 – 7j/7 pour les urgences</p>
        </div>
      </section>
    </div>
  );
};

// Ajout de Globe qui manque dans les imports
const Globe = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

export default PartenariatAgencesPage;
